import { ImageName } from "@mcleod/images";
import { Api, LogManager, Model, ModelRow, ModelRowType, StringUtil } from "@mcleod/core";
import {
    Button, Checkbox, Component, DataSource, Image, Label, Location, ModelDataChangeType, Panel, Switch, Tab, Textbox,
    Toast
} from "@mcleod/components";
import { Orders } from "./Orders";
import { AutoMatchProfile, StartingStep } from "./AutoMatchProfile";

const log = LogManager.getLogger("lme.dispatch.OrdersEquipmentMatchListener");
const badgeProps = {
    fontSize: "small",
    fontBold: true,
    color: "caution",
    borderWidth: 1,
    borderRadius: 16,
    borderColor: "caution",
    padding: 0,
    paddingLeft: 12,
    paddingRight: 12
}
const textboxProps = { printable: true, fontBold: true, width: 150, padding: 0 };

export class OrdersEquipmentMatchListener {
    private dataSource: DataSource;
    private ordersLayout: Orders;
    private idProfileNames: Map<String, String>;
    private defaultMatchIds: string[];

    constructor(order: Orders) {
        this.ordersLayout = order;
        this.dataSource = order.mainDataSource;
    }

    async rowUpdated(updateOrder: boolean = true): Promise<any> {
            const orderRow = this.dataSource.getDataboundValues(null, true, null, null);
            if (this.dataSource.activeRow?.getString("status") !== "V") {
                orderRow.set("default_match_id", null);
                return new Promise((resolve, reject) => {
                    this.ordersLayout.buttonAutoMatch.busy = true;
                    Api.search("lme/dispatch/find-matching-equipment", {
                        "order_row": orderRow
                    }).then(async response => {
                        const data = response.data?.[0];
                        if (updateOrder) {
                            await this.updateOrder(data);
                        }
                        await this.displayAutoMatch(data);
                        resolve(response);
                    }).finally(() => {
                        this.ordersLayout.buttonAutoMatch.busy = false;
                    });
                })
            }
    }

    async updateOrder(data: any) {
        if (data.brk_qual_profile_id != null && this.dataSource.activeRow.isNull("brk_qual_profile")) {
            this.dataSource.activeRow.set("brk_qual_profile", data.brk_qual_profile_id);
            const lmData = new ModelRow(this.ordersLayout.textboxBrkQualProfile.lookupModel, false, {
                id: data.brk_qual_profile_id,
                descr: data.brk_qual_profile_descr
            });
            this.dataSource.activeRow.setLookupModelData("brk_qual_profile", lmData);
            this.ordersLayout.textboxBrkQualProfile.displayData(this.dataSource.activeRow, null, 0);
        }
        if (data.default_match_ids != null)
            this.dataSource.activeRow.set("default_match_id", data.default_match_ids.join(","));

        await this.setDefaultsAndRequiredFields(data.default_values);
        await this.setAccessorials(data.accessorial_values);
        await this.setEquipmentItems(data.required_equipment || []);
    }

    async setDefaultsAndRequiredFields(defaultValues: any[]) {
        for (const defaultVal of defaultValues) {
            const row = this.dataSource.activeRow;
            const fieldName = defaultVal.field_name;
            for (const comp of this.ordersLayout.findComponentByField(fieldName)) {
                comp.required = defaultVal.required === "Y";
                if (defaultVal.default_value != null && (defaultVal.override_user == "Y" || row.isNull(fieldName))) {
                    if (comp instanceof Textbox && comp.lookupModel && comp.lookupModelLayout && comp.lookupModelResultField) {
                        await Model.searchSingleRecord(comp.lookupModel, { [comp.lookupModelResultField]: defaultVal.default_value }, comp.lookupModelLayout).then(result => {
                            if (result) {
                                row.set(fieldName, defaultVal.default_value);
                                result.type = ModelRowType.LOOKUP_MODEL_DATA;
                                row.setLookupModelData(fieldName, result);
                                comp.displayData(row, null, 0);
                                if (fieldName === "commodity_id" && (defaultVal.override_user == "Y" || row.isNull("commodity"))) {
                                    row.set("commodity", result.data["descr"]);
                                    row.set("hazmat_code", result.data["hazmat_number"]);
                                    row.set("hazmat", result.data["hazmat"]);
                                }
                            }
                        });
                    } else if (comp instanceof Location) {
                        await Model.searchSingleRecord("lme/dispatch/location", { ["id"]: defaultVal.default_value }, "lme/dispatch/LocationLookup").then(result => {
                            if (result) {
                                row.set(fieldName, defaultVal.default_value);
                                result.type = ModelRowType.LOOKUP_MODEL_DATA;
                                row.setLookupModelData(fieldName, result);
                                row.set(comp.locationIdField, result.get("id"));
                                row.set(comp.locationNameField, result.get("name"));
                                row.set(comp.cityIdField, result.get("city_id"));
                                row.set(comp.cityField, result.get("city_name"));
                                row.set(comp.stateField, result.get("state"));
                                row.set(comp.zipField, result.get("zip"));
                                row.set(comp.addressField, result.get("address1"));
                                comp.displayData(row, null, 0);
                            }
                        });
                    } else {
                        row.set(fieldName, defaultVal.default_value);
                    }
                }
            }
        }
    }

    async setAccessorials(accessorialValues: any[]) {
        const dataSource = this.ordersLayout.sourceBrltlOrderHdrXFgp;
        this.removePreviousMatches(dataSource);
        for (const accessorialVal of accessorialValues) {
            if (!this.ordersLayout.sourceBrltlOrderHdrXFgp.data.some(row => row.data["hdr_uid"] === accessorialVal.hdr_uid)) {
                const row = new ModelRow("lme/dispatch/order-handling-requirements", true, accessorialVal);
                this.ordersLayout.hdrChanged(row, ModelDataChangeType.ADD);
            }
        }
    }

    async setEquipmentItems(equipItems: any[]) {
        const dataSource = this.ordersLayout.sourceEquipMatchDetail;
        this.removePreviousMatches(dataSource);
        for (const equipItem of equipItems) {
            if (!dataSource.data.some(row =>
                row.get("equipment_match_id") === equipItem.equipment_match_id &&
                row.get("applies_to") === equipItem.applies_to &&
                row.get("equipment_type_id") === equipItem.equipment_type_id)) {
                const row = new ModelRow("lme/dispatch/equip-match-detail", true, equipItem);
                dataSource.addRow(row, dataSource.data.length, false);
            }
        }
    }

    removePreviousMatches(dataSource: DataSource) {
        for (let i = dataSource.data.length - 1; i >= 0; i--) {
            const row = dataSource.data[i];
            if (!row.isNull("equipment_match_id") &&
                !this.dataSource.activeRow.isNull("default_match_id") &&
                !this.dataSource.activeRow.get("default_match_id").includes(row.get("equipment_match_id"))) {
                dataSource._deleteTableRow(row, true);
            }
        }
    }

    async displayAutoMatch(data: any) {
        const { default_match_ids, default_values, required_equipment, comments } = data;
        this.defaultMatchIds = default_match_ids;
        this.idProfileNames = data.id_profile_names;
        const allComponents = [];
        const tabMap = {};
        const tabs = this.ordersLayout.tabsetAddOrders.components as Tab[];
        const hasMatchIds = default_match_ids.length > 0;
        for (const tab of tabs) {
            const existingAutoMatch = tab.findComponentById("imageAutoMatch");
            if (existingAutoMatch != null) {
                tab.remove(existingAutoMatch);
                const tabLabel = tab.components[0];
                tabLabel.rowBreak = tabLabel.fillRow = true;
            }
            if (tab.id == "tabOverview")
                continue;
            if (default_values.length > 0)
                await this.populateAutoMatchValuesForTab(default_values, allComponents, tab, tabMap);
            if (tab.id == "tabEquipment") {
                if (required_equipment?.length > 0)
                    this.populateRequiredEquipment(required_equipment, tab, tabMap);
            }
            if (tab.id == "tabStops") {
                if (comments?.length > 0)
                    this.populateComments(comments, tab, tabMap);
            }
        }
        if (tabMap["tabEquipment"]?.equipment) {
            allComponents.push(tabMap["tabEquipment"].equipment);
        }
        if (tabMap["tabStops"]?.comments) {
            allComponents.push(tabMap["tabStops"].comments);
        }
        this.ordersLayout.buttonAutoMatch.color = hasMatchIds ? "caution.dark" : "primary";
        if (hasMatchIds) {
            this.setAutoMatchTooltip(this.ordersLayout.buttonAutoMatch, allComponents, hasMatchIds);
        } else {
            this.ordersLayout.buttonAutoMatch.tooltip = "Auto Match";
        }
        this.displayAutoMatchTabs(tabMap);
    }

    displayAutoMatchTabs(tabMap: object) {
        for (const tab_id in tabMap) {
            const tab = tabMap[tab_id].tab;
            const components = tabMap[tab_id].components;
            if (tabMap[tab_id]?.equipment) {
                if (components.length > 0)
                    components[components.length - 1].rowBreak = true;
                components.push(tabMap[tab_id]?.equipment);
            }
            if (tabMap[tab_id]?.comments) {
                if (components.length > 0)
                    components[components.length - 1].rowBreak = true;
                components.push(tabMap[tab_id]?.comments);
            }
            this.addAutoMatchToTab(tab, components);
        }
    }

    addAutoMatchToTab(tab: Tab, components: Component[]) {
        const image = new Image({
            id: "imageAutoMatch",
            name: ImageName.autoMatch,
            color: "caution.dark",
            height: 18,
            width: 18,
            marginLeft: 4,
            rowBreak: true
        });
        const tabLabel = tab.components[0];
        tabLabel.rowBreak = tabLabel.fillRow = false;
        tab.insert(image, 1);
        this.setAutoMatchTooltip(image, components);
    }

    async populateAutoMatchValuesForTab(defaultValues: any[], allComps: Component[], tab: Tab, tabMap: object) {
        for (const info of defaultValues) {
            const comp = await this.getComponent(tab, info);
            if (comp) {
                const compPanel = new Panel({
                    id: "autoFilledField" + info.field_name,
                    components: [new Panel({ components: [comp], padding: 0, marginBottom: 4 }), this.getBadge(info)],
                    padding: 0, rowBreak: false, fillHeight: true
                });
                if (!tabMap[tab.id]) {
                    tabMap[tab.id] = { tab: tab, components: [compPanel] };
                } else {
                    tabMap[tab.id].components.push(compPanel);
                }

                allComps.push(compPanel);
            }
        }
    }

    setAutoMatchTooltip(comp: Button | Image, components: Component[], showAll: boolean = false) {
        comp.tooltipDynamicContent = () => {
            const uniqueComponents = components.filter((comp, index, self) =>
                index === self.findIndex((c) => c.id === comp.id));
            uniqueComponents.forEach(comp => {
                if (comp.parent)
                    comp.parent.remove(comp);
            });
            return this.createTooltipPanel(uniqueComponents, showAll);
        };
    }

    populateComments(comments: any[], tab: Tab, tabMap: object) {
        const commentsPanel = new Panel({ padding: 0 });
        for (const comment of comments) {
            if (!tabMap[tab.id]) {
                tabMap[tab.id] = { tab: tab, components: [] };
            }
            const orderCommentType = comment.order_comment_type;
            const commentText = `${StringUtil.isEmptyString(orderCommentType) ? "" : orderCommentType + " - "}${comment.comments}`;
            const commentLabel = new Label({
                text: commentText,
                padding: 2,
                fontSize: "small",
                width: 200,
                wrap: false
            });
            commentLabel.addClickListener((event) => {
                AutoMatchProfile.showSlideout(null, comment.parent_row_id, async (row) => {
                    await this.ordersLayout.triggerAutoMatch();
                    Toast.showSuccessToast("Profile saved successfully and Auto Match has been reapplied");
                }, StartingStep.Comments);
            });
            commentsPanel.add(commentLabel);
        }
        if (commentsPanel.components.length > 0) {
            const title = new Label({
                marginTop: 8,
                marginBottom: 4,
                padding: 0,
                text: "Comments",
                color: "subtle.dark",
                fontSize: "small"
            });
            tabMap[tab.id].comments = new Panel({ padding: 0, id: "autoMatchComments", components: [title, commentsPanel] });
        }
    }

    populateRequiredEquipment(requiredEquipment: any[], tab: Tab, tabMap: object) {
        const equipmentPanel = new Panel({ padding: 0 });
        for (const equipment of requiredEquipment) {
            if (!tabMap[tab.id]) {
                tabMap[tab.id] = { tab: tab, components: [] };
            }
            const text = `${equipment["_lookup_equipment_type_id"].descr} (${equipment.quantity}) - ` +
                this.getEquipmentDescr(equipment.applies_to);
            equipmentPanel.add(new Label({
                text, padding: 0, fontSize: "small", onClick: (event) => {
                    AutoMatchProfile.showSlideout(null, equipment.equipment_match_id, async (row) => {
                        await this.ordersLayout.triggerAutoMatch();
                        Toast.showSuccessToast("Profile saved successfully and Auto Match has been reapplied");
                    }, StartingStep.RequiredEquipment);
                }
            }));
        }
        if (equipmentPanel.components.length > 0) {
            const title = new Label({
                marginTop: 8,
                marginBottom: 4,
                padding: 0,
                text: "Required Equipment",
                color: "subtle.dark",
                fontSize: "small"
            });
            tabMap[tab.id].equipment = new Panel({ padding: 0,  id: "autoMatchRequiredEquipment", components: [title, equipmentPanel] });
        }
    }

    getDefaultMatchId(defaultMatchIds: string[]): Panel {
        return new Panel({
            paddingLeft: 0, components: [
                new Label({
                    text: "Profiles",
                    padding: 0,
                    marginRight: 4,
                    color: "subtle.dark",
                    fontSize: "large",
                    fontBold: true,
                    rowBreak: false
                }),
                ...defaultMatchIds.map((id, index) => new Button({
                    caption: StringUtil.isEmptyString(this.idProfileNames[id]) ? id : this.idProfileNames[id],
                    color: "primary",
                    minWidth: 30,
                    padding: 3,
                    rowBreak: false,
                    onClick: () => AutoMatchProfile.showSlideout(null, id, async (row) => {
                        await this.ordersLayout.triggerAutoMatch();
                        Toast.showSuccessToast("Profile saved successfully and Auto Match has been reapplied");
                    })
                }))
            ]
        });
    }

    getEquipmentDescr(appliesTo: string): string {
        switch (appliesTo) {
            case "T":
                return "Tractor";
            case "L":
                return "Trailer";
            case "Y":
                return "Tractor Item";
            case "X":
                return "Trailer Item";
            case "D":
                return "Driver Item";
        }
    }

    getBadge(info: any) {
        const badgeText = [
            info.required === "Y" ? "R" : "",
            info.default_value ? "D" : "",
            info.override_user === "Y" ? "O" : ""
        ].reduce((actual, curr) => (curr ? actual + (actual ? " " : "") + curr : actual), "");
        const tooltip: string = badgeText.split(" ").map((letter) => {
            switch (letter) {
                case "R":
                    return "R = Required";
                case "D":
                    return "D = Default";
                case "O":
                    return "O = Override User";
            }
        }).join(", ");
        const badgeLabel = new Label({ text: badgeText, ...badgeProps, tooltip: tooltip });
        badgeLabel.addClickListener((event) => {
            AutoMatchProfile.showSlideout(null, info.parent_row_id, async (row) => {
                await this.ordersLayout.triggerAutoMatch();
                Toast.showSuccessToast("Profile saved successfully and Auto Match has been reapplied");
            }, StartingStep.AutoFill);
        });
        return badgeLabel;
    }

    createTooltipPanel(components: Component[], showAll: boolean = false): Panel {
        const tooltipPanel = new Panel();
        let compPanel = null;
        tooltipPanel.insert(this.createAutoMatchTitle(showAll), 0);
        const autoFilledFields = components.filter(comp => comp.id.includes("autoFilledField"));
        const otherFields = components.filter(comp => !comp.id.includes("autoFilledField"));

        autoFilledFields.forEach(comp => {
            if (compPanel == null || compPanel.components.length >= (showAll ? 3 : 2)) {
                compPanel = new Panel({ padding: 0, marginBottom: 16 });
                tooltipPanel.add(compPanel);
            }
            compPanel.add(comp);
        });
        tooltipPanel.components[tooltipPanel.components.length - 1].rowBreak = true;
        otherFields.forEach(comp => {
            tooltipPanel.add(comp);
        });
        return tooltipPanel;
    }

    createAutoMatchTitle(includeProfiles: boolean = true) {
        const title = new Label({ text: "Auto Match", fontBold: true, fontSize: "large" });
        let components = [title];
        if (includeProfiles) {
            title.rowBreak = false;
            title.marginRight = 100;
            components = [title, ...this.getDefaultMatchId(this.defaultMatchIds)];
        }
        return new Panel({ components: components, marginBottom: 16, padding: 0 });
    }

    async getComponent(tab: Tab, info: any): Promise<Textbox | Location> {
        const comp = tab.findComponentByField(info.field_name)?.[0];
        let convertedComponent: Textbox | Location;
        if (comp) {
            const modelRow = comp.getRelevantModelRow()?.createBasicCopy();
            modelRow?.set(comp.field, info.default_value);
            if (comp instanceof Location) {
                convertedComponent = new Location({
                    ...textboxProps,
                    field: comp.field,
                    boundRow: modelRow,
                    cityField: comp.cityField,
                    stateField: comp.stateField,
                    zipField: comp.zipField,
                    id: "defaultValue" + info.field_name
                });
                convertedComponent.addressField = comp.addressField;
                convertedComponent.locationNameField = comp.locationNameField;
                convertedComponent.caption = comp.caption;
                await AutoMatchProfile.applyLookupForLocation(comp, modelRow);
            } else {
                convertedComponent = new Textbox({
                    ...textboxProps,
                    field: comp.field,
                    boundRow: modelRow,
                    id: "defaultValue" + info.field_name
                })
                if (comp instanceof Textbox || comp instanceof Switch || comp instanceof Checkbox) {
                    convertedComponent.caption = comp.caption;
                    if (comp instanceof Textbox && comp.lookupModel != null) {
                        convertedComponent.quickInfoLayout = comp.quickInfoLayout;
                        await AutoMatchProfile.applyLookupModelData(comp, modelRow)
                    }
                }
            }
            convertedComponent.displayData(modelRow, null, 0);
            return convertedComponent;
        }
        return null;
    }
}
